import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaEdit, FaTrash, FaSearch, FaBoxes, FaSync, FaFilter, FaCheckSquare, FaSquare, FaFileImport, FaTags, FaWarehouse, FaBarcode, FaPills, FaStethoscope, FaFlask, FaPlus, FaStore, FaLayerGroup, FaCheckCircle } from 'react-icons/fa';
import { updateProductPrice, deleteProduct, fetchAllProducts, searchProducts, fetchProductsBySymptom, bulkDeleteProducts, fetchProductsByCategory, fetchProductsBySupplyCategory, fetchProductsBySortimentCode, fetchProductsByMiGeLCode, fetchProductsByATCCode, fetchProductsByGenerikaCode, fetchProductsByCompany, fetchUnifiedProducts, fetchBestsellerProducts, fetchDiscountProducts, fetchUnpricedProducts, fetchBestsellers, fetchAllDiscounts, addBestseller, updateProductDiscount } from '../services/apiService';
import Pagination from './Pagination';
import ProductSearch from './ProductSearch';
import ProductAutocompleteInput from './ProductAutocompleteInput';
import ProductSearchModal from './ProductSearchModal';
import ProductFormModal from './ProductFormModal';
import ProductImportModal from './ProductImportModal';
import CategoryFilter from './CategoryFilter';
import SupplyCategoryFilter from './SupplyCategoryFilter';
import CatalogProductList from './CatalogProductList';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import SortimentCodeFilter from './SortimentCodeFilter';
import MiGeLFilter from './MiGeLFilter';
import ATCFilter from './ATCFilter';
import GenerikaFilter from './GenerikaFilter';
import CompanyDropdown from './CompanyDropdown';
import { useLanguage } from '../contexts/LanguageContext';
import ProductCard from './ProductCard';

/**
 * Build the consumerCategories.id the backend matches on:
 *   - parent only        → the parent English key (e.g. "PainFever")
 *   - parent + subcategory → the "<parentEn>_<subEn>" composite (e.g.
 *                            "PainFever_Headache")
 * Both inputs MUST be English keys. This mirrors the storefront and the
 * backend matcher (utils/categoryMatcher): a subcategory is matched by its
 * exact composite id, so admin filtering returns exactly the products the
 * storefront shows for that node — no cross-parent leak, count == result.
 */
const buildConsumerCategoryId = (categoryEn, subcategoryEn) =>
  subcategoryEn ? `${categoryEn}_${subcategoryEn}` : categoryEn;

// Add custom CSS for animations
const filterStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.3s ease-out;
  }
  
  .filter-card-hover {
    transition: all 0.2s ease-in-out;
  }
  
  .filter-card-hover:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
`;

const ProductManager = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Manual ("from scratch") product create/edit modal
  const [showProductFormModal, setShowProductFormModal] = useState(false);
  const [productFormMode, setProductFormMode] = useState('create');
  const [productFormProduct, setProductFormProduct] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isCategoryMode, setIsCategoryMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  // Localized display names for the active category filter (the *Category/
  // *Subcategory state above hold the English keys used for querying). Shown
  // in the results header so it reads in the user's language, not raw keys.
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState('');
  const [selectedSubcategoryLabel, setSelectedSubcategoryLabel] = useState('');
  const [isSupplyCategoryMode, setIsSupplyCategoryMode] = useState(false);
  const [selectedSupplyCategory, setSelectedSupplyCategory] = useState('');
  // Product scope: 'shop' (existing shop UI, unchanged) vs 'catalog' (full
  // catalog list incl. Rx/unpriced articles). Defaults to 'shop' so existing
  // behavior is preserved until the user toggles.
  const [productScope, setProductScope] = useState('shop'); // 'shop' | 'catalog'
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // Add state for filter dropdown
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(true);
  const [isSortimentMode, setIsSortimentMode] = useState(false);
  const [selectedSortimentCode, setSelectedSortimentCode] = useState('');
  const [selectedSortimentSubcode, setSelectedSortimentSubcode] = useState('');
  const [isMiGeLMode, setIsMiGeLMode] = useState(false);
  const [selectedMiGeLCode, setSelectedMiGeLCode] = useState('');
  const [selectedMiGeLSubcode, setSelectedMiGeLSubcode] = useState('');
  const [isATCMode, setIsATCMode] = useState(false);
  const [selectedATCCode, setSelectedATCCode] = useState('');
  const [selectedATCSubcode, setSelectedATCSubcode] = useState('');
  const [isGenerikaMode, setIsGenerikaMode] = useState(false);
  const [selectedGenerikaCode, setSelectedGenerikaCode] = useState('');
  const [selectedGenerikaSubcode, setSelectedGenerikaSubcode] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isCompanyMode, setIsCompanyMode] = useState(false);
  const [isBestsellerMode, setIsBestsellerMode] = useState(false);
  const [isDiscountMode, setIsDiscountMode] = useState(false);
  // Admin "rescue" view: products that have no usable price (basePrice or
  // adminRetailPrice). Driven by /admin/products/unified?onlyUnpriced=true.
  const [isUnpricedMode, setIsUnpricedMode] = useState(false);
  
  // Select all products state
  const [isSelectingAllProducts, setIsSelectingAllProducts] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);
  
  // Product import (from file) modal
  const [showProductImportModal, setShowProductImportModal] = useState(false);

  // Add bestseller/discount modals state
  const [showAddBestsellerModal, setShowAddBestsellerModal] = useState(false);
  const [isAddingBestseller, setIsAddingBestseller] = useState(false);
  const [newBestsellerPharmacode, setNewBestsellerPharmacode] = useState('');
  const [showAddDiscountModal, setShowAddDiscountModal] = useState(false);
  const [isAddingDiscount, setIsAddingDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState({ pharmacode: '', price: '' });
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isSymptomMode, setIsSymptomMode] = useState(false);
  const [selectedSymptom, setSelectedSymptom] = useState(null); // { key, label }

  // Helper: refresh discount list similar to DiscountManager
  const refreshDiscountView = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      try {
        const legacy = await fetchAllDiscounts(page, 20);
        if (legacy.success) {
          setProducts(legacy.data.products || []);
          setTotalPages(legacy.data.pages || 1);
          setCurrentPage(legacy.data.page || page);
          setTotalProducts(legacy.data.total || 0);
          setIsDiscountMode(true);
          return;
        }
      } catch (e) {
        // fallback
      }
      try {
        const unified = await fetchDiscountProducts(page, 20);
        if (unified.success) {
          setProducts(unified.items || []);
          setTotalPages(unified.pages || 1);
          setCurrentPage(unified.page || page);
          setTotalProducts(unified.count || 0);
          setIsDiscountMode(true);
          return;
        }
      } catch (e) {}
      setError(t('products.errors.fetchDiscounts'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchAllProducts(page);

      if (response.success) {
        setProducts(response.data.products);
        setTotalPages(response.data.pages);
        setCurrentPage(response.data.page);
        // Set total products count - try different possible field names
        setTotalProducts(response.data.totalProducts || response.data.total || response.data.count || 0);
        setIsSearchMode(false);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(t('products.errors.fetchProducts'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (searchQuery) => {
    try {
      setIsLoading(true);
      setError(null);
      setSearchTerm(searchQuery);
      
      const response = await searchProducts(searchQuery, 1, 20);

      if (response.success) {
        setProducts(response.data.products || []);
        setTotalPages(response.data.pagination?.totalPages || response.data.totalPages || 1);
        setCurrentPage(1);
        // Extract total products from search response pagination
        setTotalProducts(response.data.pagination?.totalProducts || response.data.totalProducts || response.data.total || 0);
        setIsSearchMode(true);
        setIsSymptomMode(false);
        setSelectedSymptom(null);
      } else {
        setProducts([]);
        setTotalPages(1);
        setCurrentPage(1);
        setTotalProducts(0);
      }
    } catch (error) {
      console.error('Error searching products:', error);
      setError(t('products.errors.searchProducts'));
      setProducts([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsSearchMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    setIsSymptomMode(false);
    setSelectedSymptom(null);
    fetchProducts(1);
  };

  const handleSymptomSelect = async (symptomKey, symptomLabel) => {
    try {
      setIsLoading(true);
      setError(null);
      setIsSearchMode(false);
      setSearchTerm('');
      setIsSymptomMode(true);
      setSelectedSymptom({ key: symptomKey, label: symptomLabel });
      setIsCategoryMode(false);
      setIsSupplyCategoryMode(false);
      setIsSortimentMode(false);
      setIsMiGeLMode(false);
      setIsATCMode(false);
      setIsGenerikaMode(false);
      setIsCompanyMode(false);
      setIsBestsellerMode(false);
      setIsDiscountMode(false);
      setIsUnpricedMode(false);

      const response = await fetchProductsBySymptom(symptomKey, 1, 20);
      if (response.success) {
        setProducts(response.data.products || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setCurrentPage(1);
        setTotalProducts(response.data.pagination?.totalProducts || 0);
      } else {
        setProducts([]); setTotalPages(1); setCurrentPage(1); setTotalProducts(0);
      }
    } catch (error) {
      console.error('Error fetching symptom products:', error);
      setError(t('products.errors.searchProducts'));
      setProducts([]); setTotalPages(1); setCurrentPage(1); setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = async (category, subcategory, categoryLabel, subcategoryLabel) => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedCategory(category);
      setSelectedSubcategory(subcategory);
      // Labels are only supplied on a fresh pick from the filter; re-filter
      // calls (refresh/pagination) omit them, so don't clobber the stored ones.
      if (categoryLabel !== undefined) setSelectedCategoryLabel(categoryLabel || '');
      if (subcategoryLabel !== undefined) setSelectedSubcategoryLabel(subcategoryLabel || '');
      setCurrentPage(1);
      
      console.log('Category filter request:', { category, subcategory }); // Debug log
      
      // Build the backend id: parent english key, or "<parentEn>_<subEn>".
      const categoryId = buildConsumerCategoryId(category, subcategory);

      if (categoryId) {
        const response = await fetchProductsByCategory(categoryId, 1, 20);
        console.log('Category filter response:', response);

        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalProducts(response.data.totalProducts || 0);
          setIsCategoryMode(true);
          setIsSearchMode(false);
          setIsSymptomMode(false);
          setSelectedSymptom(null);
        } else {
          setProducts([]);
          setTotalPages(1);
          setTotalProducts(0);
        }
      }
    } catch (error) {
      console.error('Error filtering by category:', error);
      setError(t('products.errors.filterCategory'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClear = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedCategoryLabel('');
    setSelectedSubcategoryLabel('');
    setIsCategoryMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  const handleSupplyCategorySelect = async (category) => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedSupplyCategory(category);
      setCurrentPage(1);
      
      console.log('Supply category filter request:', { category }); // Debug log
      
      if (category) {
        const response = await fetchProductsBySupplyCategory(category, 1, 20);
        console.log('Supply category filter response:', response);

        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalProducts(response.data.totalProducts || 0);
          setIsSupplyCategoryMode(true);
          setIsSearchMode(false);
          setIsCategoryMode(false);
          setIsSymptomMode(false);
          setSelectedSymptom(null);
        } else {
          setProducts([]);
          setTotalPages(1);
          setTotalProducts(0);
        }
      }
    } catch (error) {
      console.error('Error filtering by supply category:', error);
      setError(t('products.errors.filterSupplyCategory'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSupplyCategoryClear = () => {
    setSelectedSupplyCategory('');
    setIsSupplyCategoryMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  const handleSortimentSelect = async (code, subcode) => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedSortimentCode(code);
      setSelectedSortimentSubcode(subcode);
      setCurrentPage(1);
      let codeToUse = subcode || code;
      if (codeToUse) {
        const response = await fetchProductsBySortimentCode(codeToUse, 1, 20);
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalProducts(response.data.totalProducts || 0);
          setIsSortimentMode(true);
          setIsSearchMode(false);
          setIsCategoryMode(false);
          setIsSupplyCategoryMode(false);
          setIsSymptomMode(false);
          setSelectedSymptom(null);
        } else {
          setProducts([]);
          setTotalPages(1);
          setTotalProducts(0);
        }
      }
    } catch (error) {
      setError(t('products.errors.filterSortiment'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSortimentClear = () => {
    setSelectedSortimentCode('');
    setSelectedSortimentSubcode('');
    setIsSortimentMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  const handleMiGeLSelect = async (code, subcode) => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedMiGeLCode(code);
      setSelectedMiGeLSubcode(subcode);
      setCurrentPage(1);
      let codeToUse = subcode || code;
      if (codeToUse) {
        const response = await fetchProductsByMiGeLCode(codeToUse, 1, 20);
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalProducts(response.data.totalProducts || 0);
          setIsMiGeLMode(true);
          setIsSearchMode(false);
          setIsCategoryMode(false);
          setIsSupplyCategoryMode(false);
          setIsSortimentMode(false);
          setIsSymptomMode(false);
          setSelectedSymptom(null);
        } else {
          setProducts([]);
          setTotalPages(1);
          setTotalProducts(0);
        }
      }
    } catch (error) {
      setError(t('products.errors.filterMigel'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };
  const handleMiGeLClear = () => {
    setSelectedMiGeLCode('');
    setSelectedMiGeLSubcode('');
    setIsMiGeLMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  const handleATCSelect = async (code, subcode) => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedATCCode(code);
      setSelectedATCSubcode(subcode);
      setCurrentPage(1);
      let codeToUse = subcode || code;
      if (codeToUse) {
        const response = await fetchProductsByATCCode(codeToUse, 1, 20);
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalProducts(response.data.totalProducts || 0);
          setIsATCMode(true);
          setIsSearchMode(false);
          setIsCategoryMode(false);
          setIsSupplyCategoryMode(false);
          setIsSortimentMode(false);
          setIsMiGeLMode(false);
          setIsSymptomMode(false);
          setSelectedSymptom(null);
        } else {
          setProducts([]);
          setTotalPages(1);
          setTotalProducts(0);
        }
      }
    } catch (error) {
      setError(t('products.errors.filterAtc'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };
  const handleATCClear = () => {
    setSelectedATCCode('');
    setSelectedATCSubcode('');
    setIsATCMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  const handleGenerikaSelect = async (code, subcode) => {
    try {
      setIsLoading(true);
      setError(null);
      setSelectedGenerikaCode(code);
      setSelectedGenerikaSubcode(subcode);
      setCurrentPage(1);
      let codeToUse = subcode || code;
      if (codeToUse) {
        const response = await fetchProductsByGenerikaCode(codeToUse, 1, 20);
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalProducts(response.data.totalProducts || 0);
          setIsGenerikaMode(true);
          setIsSearchMode(false);
          setIsCategoryMode(false);
          setIsSupplyCategoryMode(false);
          setIsSortimentMode(false);
          setIsMiGeLMode(false);
          setIsATCMode(false);
          setIsSymptomMode(false);
          setSelectedSymptom(null);
        } else {
          setProducts([]);
          setTotalPages(1);
          setTotalProducts(0);
        }
      }
    } catch (error) {
      setError(t('products.errors.filterGenerika'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };
  const handleGenerikaClear = () => {
    setSelectedGenerikaCode('');
    setSelectedGenerikaSubcode('');
    setIsGenerikaMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  const handleCompanySelect = async (company) => {
    setIsLoading(true);
    setError(null);
    setSelectedCompany(company);
    setCurrentPage(1);
    try {
      const response = await fetchProductsByCompany(company, 1, 20);
      if (response.success) {
        setProducts(response.data.products || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalProducts(response.data.totalProducts || 0);
        setIsCompanyMode(true);
        setIsSearchMode(false);
        setIsCategoryMode(false);
        setIsSupplyCategoryMode(false);
        setIsSortimentMode(false);
        setIsMiGeLMode(false);
        setIsATCMode(false);
        setIsGenerikaMode(false);
        setIsSymptomMode(false);
        setSelectedSymptom(null);
      } else {
        setProducts([]);
        setTotalPages(1);
        setTotalProducts(0);
      }
    } catch (error) {
      setError(t('products.errors.filterCompany'));
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyClear = () => {
    setSelectedCompany('');
    setIsCompanyMode(false);
    setCurrentPage(1);
    setAllProducts([]); // Clear cached all products
    fetchProducts(1);
  };

  // Refresh the visible product list (used after a successful file import).
  const refreshProductList = async () => {
    if (isSearchMode && searchTerm) {
      await handleSearch(searchTerm);
    } else if (isCategoryMode && (selectedSubcategory || selectedCategory)) {
      await handleCategorySelect(selectedCategory, selectedSubcategory);
    } else if (isSupplyCategoryMode && selectedSupplyCategory) {
      await handleSupplyCategorySelect(selectedSupplyCategory);
    } else {
      await fetchProducts(currentPage);
    }
  };

  // Function to fetch all products using the unified endpoint
  const fetchAllProductsForSelection = async () => {
    try {
      setIsLoadingAllProducts(true);
      setError(null);
      
      // Build query parameters based on current filters
      const queryParams = {};
      
      // Add current filter parameters
      if (isSearchMode && searchTerm) {
        queryParams.searchTerms = searchTerm;
      }
      if (isCategoryMode && (selectedSubcategory || selectedCategory)) {
        queryParams.consumerCategoryId = buildConsumerCategoryId(selectedCategory, selectedSubcategory);
      }
      if (isSupplyCategoryMode && selectedSupplyCategory) {
        queryParams.supplyCategoryCode = selectedSupplyCategory;
      }
      if (isSortimentMode && (selectedSortimentSubcode || selectedSortimentCode)) {
        queryParams.sortimentCode = selectedSortimentSubcode || selectedSortimentCode;
      }
      if (isMiGeLMode && (selectedMiGeLSubcode || selectedMiGeLCode)) {
        queryParams.migelCode = selectedMiGeLSubcode || selectedMiGeLCode;
      }
      if (isATCMode && (selectedATCSubcode || selectedATCCode)) {
        queryParams.atcCode = selectedATCSubcode || selectedATCCode;
      }
      if (isGenerikaMode && (selectedGenerikaSubcode || selectedGenerikaCode)) {
        queryParams.generikaCode = selectedGenerikaSubcode || selectedGenerikaCode;
      }
      if (isCompanyMode && selectedCompany) {
        queryParams.company = selectedCompany;
      }
      
      // For bestseller/discount modes, use their endpoints with a high limit
      if (isBestsellerMode) {
        try {
          const response = await fetchBestsellerProducts(1, 200);
          if (response.success) {
            setAllProducts(response.items || []);
            return response.items || [];
          }
        } catch (e) {
          // fallback below
        }
        try {
          const legacy = await fetchBestsellers();
          if (legacy.success) {
            setAllProducts(legacy.data.products || []);
            return legacy.data.products || [];
          }
        } catch (e) {}
        setAllProducts([]);
        return [];
      }
      if (isDiscountMode) {
        try {
          const legacy = await fetchAllDiscounts(1, 200);
          if (legacy.success) {
            setAllProducts(legacy.data.products || []);
            return legacy.data.products || [];
          }
        } catch (e) {
          // fallback below
        }
        try {
          const response = await fetchDiscountProducts(1, 200);
          if (response.success) {
            setAllProducts(response.items || []);
            return response.items || [];
          }
        } catch (e) {}
        setAllProducts([]);
        return [];
      }

      // Set a high limit to get all products (backend supports up to 200)
      queryParams.limit = 200;
      queryParams.page = 1;
      const response = await fetchUnifiedProducts(queryParams);
      if (response.success) {
        setAllProducts(response.items || []);
        return response.items || [];
      }
      setAllProducts([]);
      return [];
    } catch (error) {
      console.error('Error fetching all products for selection:', error);
      setError(t('products.errors.fetchProducts'));
      setAllProducts([]);
      return [];
    } finally {
      setIsLoadingAllProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []); // Only run on mount

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (isSearchMode && searchTerm) {
      // If in search mode, search for the next page
      searchProducts(searchTerm, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.pagination?.totalPages || response.data.totalPages || 1);
          setCurrentPage(page);
          // Maintain total products count from search pagination
          setTotalProducts(response.data.pagination?.totalProducts || response.data.totalProducts || response.data.total || 0);
        }
      }).catch(error => {
        console.error('Error fetching search results page:', error);
        setError(t('products.errors.searchProducts'));
      });
    } else if (isSymptomMode && selectedSymptom) {
      fetchProductsBySymptom(selectedSymptom.key, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.pagination?.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.pagination?.totalProducts || 0);
        }
      }).catch(error => {
        console.error('Error fetching symptom results page:', error);
        setError(t('products.errors.searchProducts'));
      });
    } else if (isCategoryMode && (selectedSubcategory || selectedCategory)) {
      // If in category mode, fetch products by category for the next page
      const categoryId = buildConsumerCategoryId(selectedCategory, selectedSubcategory);
      fetchProductsByCategory(categoryId, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        console.error('Error fetching category results page:', error);
        setError(t('products.errors.filterCategory'));
      });
    } else if (isSupplyCategoryMode && selectedSupplyCategory) {
      // If in supply category mode, fetch products by supply category for the next page
      fetchProductsBySupplyCategory(selectedSupplyCategory, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        console.error('Error fetching supply category results page:', error);
        setError(t('products.errors.filterSupplyCategory'));
      });
    } else if (isSortimentMode && (selectedSortimentSubcode || selectedSortimentCode)) {
      let codeToUse = selectedSortimentSubcode || selectedSortimentCode;
      fetchProductsBySortimentCode(codeToUse, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        setError(t('products.errors.filterSortiment'));
      });
    } else if (isMiGeLMode && (selectedMiGeLSubcode || selectedMiGeLCode)) {
      let codeToUse = selectedMiGeLSubcode || selectedMiGeLCode;
      fetchProductsByMiGeLCode(codeToUse, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        setError(t('products.errors.filterMigel'));
      });
    } else if (isATCMode && (selectedATCSubcode || selectedATCCode)) {
      let codeToUse = selectedATCSubcode || selectedATCCode;
      fetchProductsByATCCode(codeToUse, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        setError(t('products.errors.filterAtc'));
      });
    } else if (isGenerikaMode && (selectedGenerikaSubcode || selectedGenerikaCode)) {
      let codeToUse = selectedGenerikaSubcode || selectedGenerikaCode;
      fetchProductsByGenerikaCode(codeToUse, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        setError(t('products.errors.filterGenerika'));
      });
    } else if (isCompanyMode && selectedCompany) {
      fetchProductsByCompany(selectedCompany, page, 20).then(response => {
        if (response.success) {
          setProducts(response.data.products || []);
          setTotalPages(response.data.totalPages || 1);
          setCurrentPage(page);
          setTotalProducts(response.data.totalProducts || 0);
        }
      }).catch(error => {
        setError(t('products.errors.filterCompany'));
      });
    } else if (isBestsellerMode) {
      (async () => {
        try {
          const response = await fetchBestsellerProducts(page, 20);
          if (response.success) {
            setProducts(response.items || []);
            setTotalPages(response.pages || 1);
            setCurrentPage(response.page || page);
            setTotalProducts(response.count || 0);
            return;
          }
        } catch (e) {
          // fallback
        }
        try {
          const legacy = await fetchBestsellers();
          if (legacy.success) {
            setProducts(legacy.data.products || []);
            setTotalPages(1);
            setCurrentPage(1);
            setTotalProducts(legacy.data.total || (legacy.data.products ? legacy.data.products.length : 0));
            return;
          }
        } catch (e) {
          setError(t('products.errors.fetchBestsellers'));
        }
      })();
    } else if (isDiscountMode) {
      (async () => {
        try {
          const legacy = await fetchAllDiscounts(page, 20);
          if (legacy.success) {
            setProducts(legacy.data.products || []);
            setTotalPages(legacy.data.pages || 1);
            setCurrentPage(legacy.data.page || page);
            setTotalProducts(legacy.data.total || 0);
            return;
          }
        } catch (e) {
          // fallback
        }
        try {
          const response = await fetchDiscountProducts(page, 20);
          if (response.success) {
            setProducts(response.items || []);
            setTotalPages(response.pages || 1);
            setCurrentPage(response.page || page);
            setTotalProducts(response.count || 0);
            return;
          }
        } catch (e) {
          setError(t('products.errors.fetchDiscounts'));
        }
      })();
    } else if (isUnpricedMode) {
      (async () => {
        try {
          const response = await fetchUnpricedProducts(page, 20);
          if (response.success) {
            setProducts(response.items || []);
            setTotalPages(response.pages || 1);
            setCurrentPage(response.page || page);
            setTotalProducts(response.count || 0);
            return;
          }
        } catch (e) {
          setError(t('products.errors.fetchProducts'));
        }
      })();
    } else {
      // If not in search, category, or supply category mode, fetch regular products
      fetchProducts(page);
    }
  };

  const handleUpdatePrice = async () => {
    // Validate first — surface every failure path with a visible, in-modal
    // message instead of silently swallowing it.
    setError(null);
    const priceNum = parseFloat(newPrice);
    if (!newPrice || isNaN(priceNum) || priceNum <= 0) {
      setError(t('products.errors.enterValidPrice'));
      return;
    }
    const pcRaw = selectedProduct?.pharmacode;
    const pcNum = Number(pcRaw);
    if (!Number.isFinite(pcNum)) {
      setError(`Invalid pharmacode on selected article: ${JSON.stringify(pcRaw)}`);
      console.error('[updatePrice] selectedProduct missing/invalid pharmacode:', selectedProduct);
      return;
    }

    console.log('[updatePrice] PUT /admin/products/' + pcNum + '/public-price', { newPrice: priceNum });
    try {
      const response = await updateProductPrice(pcNum, priceNum);
      console.log('[updatePrice] response:', response);

      if (response?.success) {
        // Refresh based on current mode so the just-priced product disappears
        // from the unpriced list and re-surfaces in the priced view.
        if (isUnpricedMode) {
          try {
            const r = await fetchUnpricedProducts(currentPage, 20);
            if (r?.success) {
              setProducts(r.items || []);
              setTotalPages(r.pages || 1);
              setCurrentPage(r.page || currentPage);
              setTotalProducts(r.count || 0);
            }
          } catch (e) {
            console.warn('[updatePrice] unpriced refresh failed:', e?.message);
          }
        } else if (isSearchMode && searchTerm) {
          await handleSearch(searchTerm);
        } else if (isCategoryMode && (selectedSubcategory || selectedCategory)) {
          await handleCategorySelect(selectedCategory, selectedSubcategory);
        } else if (isSupplyCategoryMode && selectedSupplyCategory) {
          await handleSupplyCategorySelect(selectedSupplyCategory);
        } else if (isSortimentMode && (selectedSortimentSubcode || selectedSortimentCode)) {
          await handleSortimentSelect(selectedSortimentCode, selectedSortimentSubcode);
        } else if (isMiGeLMode && (selectedMiGeLSubcode || selectedMiGeLCode)) {
          await handleMiGeLSelect(selectedMiGeLCode, selectedMiGeLSubcode);
        } else if (isATCMode && (selectedATCSubcode || selectedATCCode)) {
          await handleATCSelect(selectedATCCode, selectedATCSubcode);
        } else if (isGenerikaMode && (selectedGenerikaSubcode || selectedGenerikaCode)) {
          await handleGenerikaSelect(selectedGenerikaCode, selectedGenerikaSubcode);
        } else {
          await fetchProducts(currentPage);
        }

        setShowEditModal(false);
        setNewPrice('');
        setSelectedProduct(null);
      } else {
        // 200-ish response but success=false — surface what we got back.
        setError(response?.error || response?.data?.error || t('products.errors.updatePrice'));
      }
    } catch (error) {
      console.error('[updatePrice] FAILED:', { status: error?.response?.status, body: error?.response?.data, message: error?.message });
      const apiMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      setError(apiMsg ? `${apiMsg} (HTTP ${error?.response?.status ?? 'unknown'})` : t('products.errors.updatePrice'));
    }
  };

  const handleDelete = async () => {
    try {
      if (!deleteReason) {
        setError(t('products.errors.provideReason'));
        return;
      }

      setIsDeleting(true);
      setError(null);

      console.log('Attempting to delete product:', selectedProduct); // Debug log

      const response = await deleteProduct(selectedProduct.pharmacode, deleteReason);
      console.log('Delete response:', response); // Debug log
      
      if (response.success) {
        // Immediately update local state
        setProducts(prevProducts => {
          const updatedProducts = prevProducts.filter(product => {
            // Handle both nested articles and direct product structure
            if (product.articles && Array.isArray(product.articles)) {
              return !product.articles.some(article => 
                article.pharmacode === selectedProduct.pharmacode
              );
            } else {
              // Handle direct product structure
              const productPharmacode = product._id || product.productNumber || product.pharmacode;
              return productPharmacode !== selectedProduct.pharmacode;
            }
          });
          console.log('Updated products after deletion:', updatedProducts); // Debug log
          return updatedProducts;
        });

        // Also update total count
        setTotalProducts(prev => Math.max(0, prev - 1));
        
        setShowDeleteModal(false);
        setDeleteReason('');
        setSelectedProduct(null);
      }
    } catch (error) {
      console.error('Delete error:', error); // Debug log
      setError(error.response?.data?.message || t('products.errors.deleteProduct'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = (article, product = null) => {
    // Manual ("from scratch") products are fully editable via the product form.
    // Galexis (imported) products are catalog-read-only → only price editing.
    if (product?.source === 'manual') {
      setProductFormMode('edit');
      setProductFormProduct(product);
      setShowProductFormModal(true);
      return;
    }
    setSelectedProduct(article);
    setShowEditModal(true);
  };

  /**
   * Optimistic local-state update fired by the inline PriceEditor on a
   * card after a successful PUT /admin/products/:pc/public-price.
   *
   *   • In `isUnpricedMode`, the just-priced product no longer belongs
   *     in the rescue list — drop it from `products` immediately.
   *     (The backend already uses ES `refresh: 'wait_for'`, so a fresh
   *     fetch would agree; this avoids a network round-trip.)
   *   • In any other mode, splice the new price into the matching
   *     article in-place so PriceDisplay re-renders without re-fetching.
   *
   * Errors are surfaced inside PriceEditor itself (per-row); this fires
   * only on success.
   */
  const handlePriceSaved = ({ pharmacode, basePrice, adminRetailPrice }) => {
    const pcNum = Number(pharmacode);
    const matches = (a) => Number(a?.pharmacode) === pcNum;

    if (isUnpricedMode) {
      setProducts((prev) =>
        prev.filter((p) => !(p.articles || []).some(matches))
      );
      setTotalProducts((n) => Math.max(0, (n || 0) - 1));
      return;
    }

    setProducts((prev) =>
      prev.map((p) => {
        if (!(p.articles || []).some(matches)) return p;
        return {
          ...p,
          articles: p.articles.map((a) =>
            matches(a)
              ? {
                  ...a,
                  basePrice,
                  adminRetailPrice,
                  price: {
                    ...(a.price || {}),
                    currentPrice: basePrice,
                    basePrice,
                    originalPrice: basePrice,
                  },
                }
              : a
          ),
        };
      })
    );
  };

  const handleCreateProductClick = () => {
    setProductFormMode('create');
    setProductFormProduct(null);
    setShowProductFormModal(true);
  };

  // Re-fetch the current view after a manual product is created/edited so it
  // appears/updates immediately, mirroring how the price/delete flows refresh.
  const handleProductFormSaved = async () => {
    setSuccessMessage(
      productFormMode === 'edit'
        ? t('products.productForm.success.updated')
        : t('products.productForm.success.created')
    );
    setTimeout(() => setSuccessMessage(''), 5000);
    if (isSearchMode && searchTerm) {
      await handleSearch(searchTerm);
    } else if (isCategoryMode && (selectedSubcategory || selectedCategory)) {
      await handleCategorySelect(selectedCategory, selectedSubcategory);
    } else if (isSupplyCategoryMode && selectedSupplyCategory) {
      await handleSupplyCategorySelect(selectedSupplyCategory);
    } else {
      await fetchProducts(currentPage);
    }
  };

  const handleDeleteClick = (article) => {
    setSelectedProduct(article);
    setShowDeleteModal(true);
  };

  const handleSelectProduct = (product) => {
    setSelectedProducts(prevSelected => {
      const isAlreadySelected = prevSelected.some(p => p.pharmacode === product.pharmacode);
      if (isAlreadySelected) {
        return prevSelected.filter(p => p.pharmacode !== product.pharmacode);
      } else {
        return [...prevSelected, product];
      }
    });
  };

  const handleExitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedProducts([]);
    setAllProducts([]);
    setIsSelectingAllProducts(false);
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      setError(t('products.errors.selectOne'));
      return;
    }

    if (!deleteReason) {
      setError(t('products.errors.provideBulkReason'));
      return;
    }

    try {
      setIsBulkDeleting(true);
      setError(null);

      console.log('Starting bulk delete with:', {
        selectedProducts: selectedProducts.map(p => p.pharmacode),
        reason: deleteReason
      });

      const response = await bulkDeleteProducts(selectedProducts.map(p => p.pharmacode), deleteReason);
      console.log('Bulk delete response:', response);

      if (response.success) {
        setProducts(prevProducts => prevProducts.filter(product => {
          // Handle both nested articles and direct product structure
          if (product.articles && Array.isArray(product.articles)) {
            return !product.articles.some(article => 
              selectedProducts.some(selected => selected.pharmacode === article.pharmacode)
            );
          } else {
            // Handle direct product structure
            const productPharmacode = product._id || product.productNumber || product.pharmacode;
            return !selectedProducts.some(selected => selected.pharmacode === productPharmacode);
          }
        }));
        
        // Update total count
        const deletedCount = response.data.deletedCount || selectedProducts.length;
        setTotalProducts(prev => Math.max(0, prev - deletedCount));
        
        setSelectedProducts([]);
        setIsBulkDeleteModalOpen(false);
        setDeleteReason('');
        setError(null);
        setSuccessMessage(t('products.success.bulkDelete', { count: deletedCount }));
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setError(response.message || t('products.errors.bulkDelete'));
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      
      // Provide more detailed error information
      let errorMessage = 'Failed to delete products.';
      
      if (error.response?.data?.error) {
        // Handle the specific backend error about UNAUTHORIZED
        if (error.response.data.error.includes('UNAUTHORIZED')) {
          errorMessage = 'Authentication error. Please log in again.';
          // Optionally redirect to login
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          errorMessage = error.response.data.error;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred while deleting products. Please try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete products.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Add click outside handler for filter dropdown
  useEffect(() => {
    if (!isFilterDropdownOpen) return;
    function handleClick(e) {
      const dropdown = document.querySelector('.z-30');
      if (dropdown && !dropdown.contains(e.target)) {
        setIsFilterDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isFilterDropdownOpen]);

  return (
    <div className="bg-white">
      {/* Add custom styles */}
      <style>{filterStyles}</style>
      
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6 pt-6 sm:pt-8">
        {/* Scope toggle: Shop vs full catalog — two modern, full-width cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Shop scope */}
          <button
            type="button"
            onClick={() => setProductScope('shop')}
            aria-pressed={productScope === 'shop'}
            className={`group relative text-left rounded-2xl border p-5 transition-all duration-200 ${
              productScope === 'shop'
                ? 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-blue-50 ring-2 ring-indigo-400 shadow-md'
                : 'border-gray-200 bg-white hover:border-indigo-200 hover:shadow-sm'
            }`}
          >
            {productScope === 'shop' && (
              <FaCheckCircle className="absolute top-4 right-4 text-indigo-500 text-lg" />
            )}
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm transition-colors ${
                productScope === 'shop'
                  ? 'bg-gradient-to-br from-indigo-500 to-blue-600 text-white'
                  : 'bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-600'
              }`}>
                <FaStore className="text-xl" />
              </div>
              <div>
                <h3 className={`font-semibold ${productScope === 'shop' ? 'text-indigo-900' : 'text-gray-800'}`}>
                  {t('products.scope.shop')}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">{t('products.scope.shopDescription')}</p>
              </div>
            </div>
          </button>

          {/* All catalog scope */}
          <button
            type="button"
            onClick={() => setProductScope('catalog')}
            aria-pressed={productScope === 'catalog'}
            className={`group relative text-left rounded-2xl border p-5 transition-all duration-200 ${
              productScope === 'catalog'
                ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 ring-2 ring-emerald-400 shadow-md'
                : 'border-gray-200 bg-white hover:border-emerald-200 hover:shadow-sm'
            }`}
          >
            {productScope === 'catalog' && (
              <FaCheckCircle className="absolute top-4 right-4 text-emerald-500 text-lg" />
            )}
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm transition-colors ${
                productScope === 'catalog'
                  ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                  : 'bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600'
              }`}>
                <FaLayerGroup className="text-xl" />
              </div>
              <div>
                <h3 className={`font-semibold ${productScope === 'catalog' ? 'text-emerald-900' : 'text-gray-800'}`}>
                  {t('products.scope.catalog')}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">{t('products.scope.catalogDescription')}</p>
              </div>
            </div>
          </button>
        </div>

        {productScope === 'catalog' ? (
          <CatalogProductList />
        ) : (
        <>
        {/* Modern Header Section */}
        <div className="border-b border-gray-200 pb-8 pt-10 overflow-visible">
          {/* Main Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-3">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <FaBoxes className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  {t('products.title')}
                  {isSelectionMode && (
                    <span className="ml-3 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg text-lg font-medium text-indigo-700">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                      {t('products.selectionMode')}
                    </span>
                  )}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {isSelectionMode 
                    ? t('products.subtitleSelectionMode')
                    : t('products.subtitle')
                  }
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  if (isSearchMode && searchTerm) {
                    handleSearch(searchTerm);
                  } else if (isCategoryMode && (selectedSubcategory || selectedCategory)) {
                    handleCategorySelect(selectedCategory, selectedSubcategory);
                  } else if (isSupplyCategoryMode && selectedSupplyCategory) {
                    handleSupplyCategorySelect(selectedSupplyCategory);
                  } else if (isSortimentMode && (selectedSortimentSubcode || selectedSortimentCode)) {
                    handleSortimentSelect(selectedSortimentCode, selectedSortimentSubcode);
                  } else if (isMiGeLMode && (selectedMiGeLSubcode || selectedMiGeLCode)) {
                    handleMiGeLSelect(selectedMiGeLCode, selectedMiGeLSubcode);
                  } else if (isATCMode && (selectedATCSubcode || selectedATCCode)) {
                    handleATCSelect(selectedATCCode, selectedATCSubcode);
                  } else if (isGenerikaMode && (selectedGenerikaSubcode || selectedGenerikaCode)) {
                    handleGenerikaSelect(selectedGenerikaCode, selectedGenerikaSubcode);
                  } else if (isCompanyMode && selectedCompany) {
                    handleCompanySelect(selectedCompany);
                  } else if (isBestsellerMode) {
                    (async () => {
                      try {
                        const response = await fetchBestsellerProducts(currentPage, 20);
                        if (response.success) {
                          setProducts(response.items || []);
                          setTotalPages(response.pages || 1);
                          setTotalProducts(response.count || 0);
                          return;
                        }
                      } catch (e) {
                        // fallback
                      }
                      try {
                        const legacy = await fetchBestsellers();
                        if (legacy.success) {
                          setProducts(legacy.data.products || []);
                          setTotalPages(1);
                          setCurrentPage(1);
                          setTotalProducts(legacy.data.total || (legacy.data.products ? legacy.data.products.length : 0));
                        }
                      } catch (e) {}
                    })();
                  } else if (isDiscountMode) {
                    (async () => {
                      try {
                        const legacy = await fetchAllDiscounts(currentPage, 20);
                        if (legacy.success) {
                          setProducts(legacy.data.products || []);
                          setTotalPages(legacy.data.pages || 1);
                          setTotalProducts(legacy.data.total || 0);
                          return;
                        }
                      } catch (e) {
                        // fallback
                      }
                      try {
                        const response = await fetchDiscountProducts(currentPage, 20);
                        if (response.success) {
                          setProducts(response.items || []);
                          setTotalPages(response.pages || 1);
                          setTotalProducts(response.count || 0);
                        }
                      } catch (e) {}
                    })();
                  } else {
                    fetchProducts(currentPage);
                  }
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaSync className="text-sm" />
                {t('products.refreshProducts')}
              </button>
              
              <button
                onClick={handleCreateProductClick}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2.5 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaPlus className="text-sm" />
                {t('products.createProduct')}
              </button>

              <button
                onClick={() => setShowProductImportModal(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2.5 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaFileImport className="text-sm" />
                {t('products.importFromFile')}
              </button>
              
              <button
                onClick={() => navigate('/products/deleted')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2.5 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaTrash className="text-sm" />
                {t('products.viewDeletedProducts')}
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">{t('products.stats.totalProducts')}</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {isLoading ? (
                      <div className="animate-pulse bg-blue-200 h-8 w-16 rounded"></div>
                    ) : (
                      totalProducts.toLocaleString()
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <FaBoxes className="text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">{t('products.stats.currentPage')}</p>
                  <p className="text-2xl font-bold text-green-900">
                    {currentPage} of {totalPages}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <FaFilter className="text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">
                    {isSearchMode ? t('products.stats.searchResults') : isCategoryMode ? t('products.stats.categoryResults') : isSupplyCategoryMode ? t('products.stats.supplyCategoryResults') : isMiGeLMode ? t('products.stats.migelResults') : isATCMode ? t('products.stats.atcResults') : isGenerikaMode ? t('products.stats.generikaResults') : isCompanyMode ? t('products.stats.companyResults') : isBestsellerMode ? t('products.stats.bestsellerResults') : isDiscountMode ? t('products.stats.discountResults') : t('products.stats.showing')}
                  </p>
                  <p className="text-2xl font-bold text-purple-900">
                    {products.length} {t('products.stats.products')}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                  {isSearchMode ? (
                    <FaSearch className="text-purple-600" />
                  ) : isCategoryMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isSupplyCategoryMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isMiGeLMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isATCMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isGenerikaMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isCompanyMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isBestsellerMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : isDiscountMode ? (
                    <FaFilter className="text-purple-600" />
                  ) : (
                    <FaBoxes className="text-purple-600" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters Row */}
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* Search Bar on the left */}
            <div className="flex-1 order-1 lg:order-none">
              <ProductSearch
                onSearch={handleSearch}
                onClear={handleClearSearch}
                onSymptomSelect={handleSymptomSelect}
                placeholder="Search products by pharmacode, name, or brand..."
              />
              {isSymptomMode && selectedSymptom && (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-cyan-50 border border-cyan-100 px-4 py-2">
                  <span className="text-lg">🩹</span>
                  <span className="flex-1 text-sm text-cyan-800">
                    {t('filters.productSearch.symptomResultsHeader')}{' '}
                    <span className="font-semibold">{selectedSymptom.label}</span>
                  </span>
                  <button type="button" onClick={handleClearSearch} className="text-sm text-cyan-700 underline hover:text-cyan-900">
                    {t('filters.productSearch.symptomClear')}
                  </button>
                </div>
              )}
            </div>

            {/* Company Dropdown in the middle */}
            <div className="flex flex-row gap-3 items-center flex-shrink-0 order-2 lg:order-none">
              <div className="flex items-center gap-2">
                <CompanyDropdown
                  onSelect={handleCompanySelect}
                  selectedCompany={selectedCompany}
                />
                {selectedCompany && (
                  <button
                    onClick={handleCompanyClear}
                    className="ml-2 text-xs text-gray-500 underline"
                  >
                    {t('products.filters.clear')}
                  </button>
                )}
              </div>
            </div>

            {/* Filters button on the right */}
            <div className="flex-shrink-0 order-3 lg:order-none">
              <button
                onClick={() => setIsFilterDropdownOpen((open) => !open)}
                className={`inline-flex items-center gap-3 px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl min-w-[180px] ${
                  isFilterDropdownOpen 
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-700 text-white shadow-indigo-200' 
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                }`}
              >
                <FaFilter className={`text-lg ${isFilterDropdownOpen ? 'text-white' : 'text-indigo-500'}`} />
                <span>{t('products.filters.title')}</span>
                {isFilterDropdownOpen && (
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                )}
              </button>
            </div>
          </div>

          {/* Inline Filters Section */}
          {isFilterDropdownOpen && (
            <div className="mt-6 animate-fade-in">
              {/* Filter Content - Vertical Layout */}
              <div className="space-y-4">
                {/* Row 1: Category and Supply */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Category Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <CategoryFilter
                          onCategorySelect={(cat, subcat, catLabel, subLabel) => {
                            handleCategorySelect(cat, subcat, catLabel, subLabel);
                          }}
                          onClear={() => {
                            handleCategoryClear();
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.category.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.category.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaTags className="text-emerald-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Supply Category Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <SupplyCategoryFilter
                          onCategorySelect={(cat) => {
                            handleSupplyCategorySelect(cat);
                          }}
                          onClear={() => {
                            handleSupplyCategoryClear();
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.supply.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.supply.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaWarehouse className="text-blue-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Sortiment and MiGeL */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sortiment Code Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <SortimentCodeFilter
                          onSortimentCodeSelect={(code, subcode) => {
                            handleSortimentSelect(code, subcode);
                          }}
                          onClear={() => {
                            handleSortimentClear();
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.sortiment.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.sortiment.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaBarcode className="text-amber-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MiGeL Code Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <MiGeLFilter
                          onMiGeLCodeSelect={(code, subcode) => {
                            handleMiGeLSelect(code, subcode);
                          }}
                          onClear={() => {
                            handleMiGeLClear();
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.migel.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.migel.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaPills className="text-purple-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: ATC and Generika */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ATC Code Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <ATCFilter
                          onATCCodeSelect={(code, subcode) => {
                            handleATCSelect(code, subcode);
                          }}
                          onClear={() => {
                            handleATCClear();
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.atc.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.atc.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaStethoscope className="text-cyan-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Generika Code Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <GenerikaFilter
                          onGenerikaCodeSelect={(code, subcode) => {
                            handleGenerikaSelect(code, subcode);
                          }}
                          onClear={() => {
                            handleGenerikaClear();
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.generika.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.generika.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaFlask className="text-orange-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Bestseller and Discount */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bestseller Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={async () => {
                              setIsLoading(true);
                              setError(null);
                              setCurrentPage(1);
                              try {
                                // Try unified first
                                const response = await fetchBestsellerProducts(1, 20);
                                if (response.success) {
                                  setProducts(response.items || []);
                                  setTotalPages(response.pages || 1);
                                  setTotalProducts(response.count || 0);
                                } else {
                                  throw new Error('Unified bestseller failed');
                                }
                              } catch (e) {
                                // Fallback to legacy
                                try {
                                  const legacy = await fetchBestsellers();
                                  if (legacy.success) {
                                    setProducts(legacy.data.products || []);
                                    setTotalPages(1);
                                    setTotalProducts(legacy.data.total || (legacy.data.products ? legacy.data.products.length : 0));
                                  } else {
                                    throw new Error('Legacy bestseller failed');
                                  }
                                } catch (err) {
                                  setError(t('products.errors.fetchBestsellers'));
                                  setProducts([]);
                                  setTotalPages(1);
                                  setTotalProducts(0);
                                }
                              } finally {
                                setIsLoading(false);
                              }
                              setIsBestsellerMode(true);
                              // turn off others
                              setIsDiscountMode(false);
                              setIsUnpricedMode(false);
                              setIsSearchMode(false);
                              setIsCategoryMode(false);
                              setIsSupplyCategoryMode(false);
                              setIsSortimentMode(false);
                              setIsMiGeLMode(false);
                              setIsATCMode(false);
                              setIsGenerikaMode(false);
                              setIsCompanyMode(false);
                              setIsSymptomMode(false);
                              setSelectedSymptom(null);
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${isBestsellerMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {t('products.filters.bestseller.showButton')}
                          </button>
                          {isBestsellerMode && (
                            <button
                              onClick={() => {
                                setIsBestsellerMode(false);
                                setCurrentPage(1);
                                setAllProducts([]);
                                fetchProducts(1);
                              }}
                              className="text-xs text-gray-500 underline"
                            >
                              {t('products.filters.clear')}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.bestseller.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.bestseller.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaTags className="text-orange-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Discount Filter */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={async () => {
                              setIsLoading(true);
                              setError(null);
                              setCurrentPage(1);
                              try {
                                // Try legacy first
                                const legacy = await fetchAllDiscounts(1, 20);
                                if (legacy.success) {
                                  setProducts(legacy.data.products || []);
                                  setTotalPages(legacy.data.pages || 1);
                                  setTotalProducts(legacy.data.total || 0);
                                } else {
                                  throw new Error('Legacy discounts failed');
                                }
                              } catch (e) {
                                // Fallback to unified
                                try {
                                  const response = await fetchDiscountProducts(1, 20);
                                  if (response.success) {
                                    setProducts(response.items || []);
                                    setTotalPages(response.pages || 1);
                                    setTotalProducts(response.count || 0);
                                  } else {
                                    throw new Error('Unified discounts failed');
                                  }
                                } catch (err) {
                                  setError(t('products.errors.fetchDiscounts'));
                                  setProducts([]);
                                  setTotalPages(1);
                                  setTotalProducts(0);
                                }
                              } finally {
                                setIsLoading(false);
                              }
                              setIsDiscountMode(true);
                              // turn off others
                              setIsBestsellerMode(false);
                              setIsUnpricedMode(false);
                              setIsSearchMode(false);
                              setIsCategoryMode(false);
                              setIsSupplyCategoryMode(false);
                              setIsSortimentMode(false);
                              setIsMiGeLMode(false);
                              setIsATCMode(false);
                              setIsGenerikaMode(false);
                              setIsCompanyMode(false);
                              setIsSymptomMode(false);
                              setSelectedSymptom(null);
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${isDiscountMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {t('products.filters.discounts.showButton')}
                          </button>
                          {isDiscountMode && (
                            <button
                              onClick={() => {
                                setIsDiscountMode(false);
                                setCurrentPage(1);
                                setAllProducts([]);
                                fetchProducts(1);
                              }}
                              className="text-xs text-gray-500 underline"
                            >
                              {t('products.filters.clear')}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.discounts.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.discounts.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-pink-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaTags className="text-red-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Unpriced Filter — admin "rescue" view for items with no usable price.
                      Hits /admin/products/unified?onlyUnpriced=true on the backend. Admin
                      sets a retail price via the existing inline editor; the next refresh
                      drops the product from this list and surfaces it in the customer shop. */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={async () => {
                              setIsLoading(true);
                              setError(null);
                              setCurrentPage(1);
                              try {
                                const response = await fetchUnpricedProducts(1, 20);
                                if (response.success) {
                                  setProducts(response.items || []);
                                  setTotalPages(response.pages || 1);
                                  setTotalProducts(response.count || 0);
                                } else {
                                  throw new Error('Unpriced fetch failed');
                                }
                              } catch (err) {
                                setError(t('products.errors.fetchProducts'));
                                setProducts([]);
                                setTotalPages(1);
                                setTotalProducts(0);
                              } finally {
                                setIsLoading(false);
                              }
                              setIsUnpricedMode(true);
                              // turn off others
                              setIsBestsellerMode(false);
                              setIsDiscountMode(false);
                              setIsSearchMode(false);
                              setIsCategoryMode(false);
                              setIsSupplyCategoryMode(false);
                              setIsSortimentMode(false);
                              setIsMiGeLMode(false);
                              setIsATCMode(false);
                              setIsGenerikaMode(false);
                              setIsCompanyMode(false);
                              setIsSymptomMode(false);
                              setSelectedSymptom(null);
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${isUnpricedMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {t('products.filters.unpriced.showButton')}
                          </button>
                          {isUnpricedMode && (
                            <button
                              onClick={() => {
                                setIsUnpricedMode(false);
                                setCurrentPage(1);
                                setAllProducts([]);
                                fetchProducts(1);
                              }}
                              className="text-xs text-gray-500 underline"
                            >
                              {t('products.filters.clear')}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <h4 className="font-semibold text-gray-800">{t('products.filters.unpriced.title')}</h4>
                          <p className="text-sm text-gray-600">{t('products.filters.unpriced.description')}</p>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl flex items-center justify-center shadow-sm">
                          <FaTags className="text-amber-600 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clear All Filters Button */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FaSync className="text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">{t('products.filters.clearAll')}</p>
                      <p className="text-sm text-gray-500">{t('products.filters.clearAllDesc')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleCategoryClear();
                      handleSupplyCategoryClear();
                      handleSortimentClear();
                      handleMiGeLClear();
                      handleATCClear();
                      handleGenerikaClear();
                      handleCompanyClear();
                      setIsBestsellerMode(false);
                      setIsDiscountMode(false);
                    }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium hover:from-gray-200 hover:to-gray-300 transition-all duration-200 shadow-sm border border-gray-300"
                  >
                    <FaSync className="text-gray-500" />
                    {t('products.filters.clearAll')}
                  </button>
                </div>
              </div>
            </div>
          )}


        </div>

        {/* Selection Mode Controls - Seamless and modern */}
        {isSelectionMode && (
          <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      totalPages > 1 
                        ? selectedProducts.length === totalProducts && totalProducts > 0
                        : selectedProducts.length === products.length && products.length > 0
                    }
                    onChange={async (e) => {
                      if (e.target.checked) {
                        // Check if we need to fetch all products or just use current page
                        if (totalPages > 1) {
                          // If there are multiple pages, fetch all products using unified endpoint
                          setIsSelectingAllProducts(true);
                          const allProductsData = await fetchAllProductsForSelection();
                          
                          if (allProductsData.length > 0) {
                            const allArticles = allProductsData.map(product => {
                              // Extract article from product structure
                              if (product.articles && Array.isArray(product.articles)) {
                                const firstArticle = product.articles[0];
                                // Handle images - can be array directly (search results) or object with M property (regular products)
                                let imagesArray = [];
                                if (Array.isArray(firstArticle.images)) {
                                  imagesArray = firstArticle.images;
                                } else if (Array.isArray(firstArticle.images?.M)) {
                                  imagesArray = firstArticle.images.M;
                                }
                                
                                return {
                                  pharmacode: firstArticle.pharmacode,
                                  description: {
                                    description: firstArticle.description?.description || 'No description available',
                                    longDescription: firstArticle.description?.longDescription || 'No description available'
                                  },
                                  hasPhoto: firstArticle.hasPhoto || false,
                                  images: {
                                    M: imagesArray.map(img => img.replace(/^uploads\\/, ''))
                                  },
                                  price: firstArticle.price || firstArticle.prices || firstArticle.priceInfo || null
                                };
                              } else {
                                const description = product.description;
                                let descriptionText = 'No description available';
                                let longDescriptionText = 'No long description available';
                                
                                if (typeof description === 'string') {
                                  descriptionText = description;
                                  longDescriptionText = description;
                                } else if (description && typeof description === 'object') {
                                  descriptionText = description.description || description.longDescription || 'No description available';
                                  longDescriptionText = description.longDescription || description.description || 'No long description available';
                                }
                                
                                return {
                                  pharmacode: product.pharmacode || product.productNumber || product._id,
                                  description: {
                                    description: descriptionText,
                                    longDescription: longDescriptionText
                                  },
                                  hasPhoto: product.hasPhoto || false,
                                  images: {
                                    M: Array.isArray(product.images)
                                      ? product.images.map(img => img.replace(/^uploads\\/, ''))
                                      : Array.isArray(product.images?.M)
                                        ? product.images.M.map(img => img.replace(/^uploads\\/, ''))
                                        : []
                                  },
                                  price: product.prices || product.priceInfo || null
                                };
                              }
                            });
                            setSelectedProducts(allArticles);
                          }
                          setIsSelectingAllProducts(false);
                        } else {
                          // If only one page, use current products
                          setSelectedProducts(products.map(product => {
                            // Extract article from product structure
                            if (product.articles && Array.isArray(product.articles)) {
                              const firstArticle = product.articles[0];
                              // Handle images - can be array directly (search results) or object with M property (regular products)
                              let imagesArray = [];
                              if (Array.isArray(firstArticle.images)) {
                                imagesArray = firstArticle.images;
                              } else if (Array.isArray(firstArticle.images?.M)) {
                                imagesArray = firstArticle.images.M;
                              }
                              
                              return {
                                pharmacode: firstArticle.pharmacode,
                                description: {
                                  description: firstArticle.description?.description || 'No description available',
                                  longDescription: firstArticle.description?.longDescription || 'No description available'
                                },
                                hasPhoto: firstArticle.hasPhoto || false,
                                images: {
                                  M: imagesArray.map(img => img.replace(/^uploads\\/, ''))
                                },
                                price: firstArticle.price || firstArticle.prices || firstArticle.priceInfo || null
                              };
                            } else {
                              const description = product.description;
                              let descriptionText = 'No description available';
                              let longDescriptionText = 'No long description available';
                              
                              if (typeof description === 'string') {
                                descriptionText = description;
                                longDescriptionText = description;
                              } else if (description && typeof description === 'object') {
                                descriptionText = description.description || description.longDescription || 'No description available';
                                longDescriptionText = description.longDescription || description.description || 'No long description available';
                              }
                              
                              return {
                                pharmacode: product.pharmacode || product.productNumber || product._id,
                                description: {
                                  description: descriptionText,
                                  longDescription: longDescriptionText
                                },
                                hasPhoto: product.hasPhoto || false,
                                images: {
                                  M: Array.isArray(product.images)
                                    ? product.images.map(img => img.replace(/^uploads\\/, ''))
                                    : Array.isArray(product.images?.M)
                                      ? product.images.M.map(img => img.replace(/^uploads\\/, ''))
                                      : []
                                },
                                price: product.prices || product.priceInfo || null
                              };
                            }
                          }));
                        }
                      } else {
                        setSelectedProducts([]);
                      }
                    }}
                    disabled={isLoadingAllProducts}
                    className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {isLoadingAllProducts ? (
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="sm" variant="gray" />
                        {t('products.selection.loadingAll')}
                      </span>
                    ) : totalPages > 1 ? (
                      t('products.selection.selectAllTotal', { total: totalProducts })
                    ) : (
                      `${t('products.selection.selectAll')} (${products.length})`
                    )}
                  </span>
                </div>
                {selectedProducts.length > 0 && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-md">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
                    <span className="text-sm font-medium text-indigo-700">
                      {selectedProducts.length} {t('products.selection.selected')}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {selectedProducts.length > 0 && (
                  <button
                    onClick={() => {
                      setDeleteReason('');
                      setIsBulkDeleteModalOpen(true);
                    }}
                    disabled={isBulkDeleting}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      isBulkDeleting
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                    }`}
                  >
                    {isBulkDeleting ? (
                      <>
                        <LoadingSpinner size="sm" variant="gray" />
                        {t('products.selection.deleting')}
                      </>
                    ) : (
                      <>
                        <FaTrash className="text-xs" />
                        {t('products.selection.delete')} ({selectedProducts.length})
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleExitSelectionMode}
                  className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
                >
                  {t('products.selection.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <FaCheckSquare className="h-5 w-5 text-green-600" />
            {successMessage}
          </div>
        )}

        <section aria-labelledby="products-heading" className="pb-24 pt-6">
          {/* Search Results Header */}
          {isSearchMode && searchTerm && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaSearch className="text-blue-600" />
                  <span className="text-blue-900 font-medium">
                    {t('products.search.results', { searchTerm })}
                  </span>
                </div>
                <button
                  onClick={handleClearSearch}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {t('products.search.clearSearch')}
                </button>
              </div>
            </div>
          )}

          {/* Category Results Header */}
          {isCategoryMode && (selectedSubcategory || selectedCategory) && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-green-600" />
                  <span className="text-green-900 font-medium">
                    {t('products.filterResults.category', { category: selectedSubcategoryLabel || selectedCategoryLabel || selectedSubcategory || selectedCategory })}
                  </span>
                </div>
                <button
                  onClick={handleCategoryClear}
                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {/* Supply Category Results Header */}
          {isSupplyCategoryMode && selectedSupplyCategory && (
            <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-indigo-600" />
                  <span className="text-indigo-900 font-medium">
                    {t('products.filterResults.supplyCategory', { category: selectedSupplyCategory })}
                  </span>
                </div>
                <button
                  onClick={handleSupplyCategoryClear}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isMiGeLMode && (selectedMiGeLSubcode || selectedMiGeLCode) && (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-purple-600" />
                  <span className="text-purple-900 font-medium">
                    {t('products.filterResults.migel', { code: selectedMiGeLSubcode || selectedMiGeLCode })}
                  </span>
                </div>
                <button
                  onClick={handleMiGeLClear}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isATCMode && (selectedATCSubcode || selectedATCCode) && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-blue-600" />
                  <span className="text-blue-900 font-medium">
                    {t('products.filterResults.atc', { code: selectedATCSubcode || selectedATCCode })}
                  </span>
                </div>
                <button
                  onClick={handleATCClear}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isGenerikaMode && (selectedGenerikaSubcode || selectedGenerikaCode) && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-orange-600" />
                  <span className="text-orange-900 font-medium">
                    {t('products.filterResults.generika', { code: selectedGenerikaSubcode || selectedGenerikaCode })}
                  </span>
                </div>
                <button
                  onClick={handleGenerikaClear}
                  className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isSortimentMode && (selectedSortimentSubcode || selectedSortimentCode) && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-yellow-600" />
                  <span className="text-yellow-900 font-medium">
                    {t('products.filterResults.sortiment', { code: selectedSortimentSubcode || selectedSortimentCode })}
                  </span>
                </div>
                <button
                  onClick={handleSortimentClear}
                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isBestsellerMode && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-yellow-600" />
                  <span className="text-yellow-900 font-medium">{t('products.filterResults.bestseller')}</span>
                </div>
                <button
                  onClick={() => {
                    setIsBestsellerMode(false);
                    setCurrentPage(1);
                    setAllProducts([]);
                    fetchProducts(1);
                  }}
                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isDiscountMode && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFilter className="text-red-600" />
                  <span className="text-red-900 font-medium">{t('products.filterResults.discounts')}</span>
                </div>
                <button
                  onClick={() => {
                    setIsDiscountMode(false);
                    setCurrentPage(1);
                    setAllProducts([]);
                    fetchProducts(1);
                  }}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {isCompanyMode && selectedCompany && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 font-bold">🏢</span>
                  <span className="text-gray-900 font-medium">
                    {t('products.filterResults.company', { company: selectedCompany })}
                  </span>
                </div>
                <button
                  onClick={handleCompanyClear}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  {t('products.filterResults.clearFilter')}
                </button>
              </div>
            </div>
          )}

          {/* Actions above grid: contextual add + select */}
          {products.length > 0 && !isSelectionMode && (
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isBestsellerMode && (
                  <button
                    onClick={() => setShowAddBestsellerModal(true)}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-1.5 rounded-md hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow"
                  >
                    <FaEdit className="text-xs" />
                    {t('products.actions.addBestseller')}
                  </button>
                )}
                {isDiscountMode && (
                  <button
                    onClick={() => setShowAddDiscountModal(true)}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-600 text-white px-3 py-1.5 rounded-md hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-sm hover:shadow"
                  >
                    <FaEdit className="text-xs" />
                    {t('products.actions.addDiscount')}
                  </button>
                )}
              </div>
              <div>
                <button
                  onClick={() => setIsSelectionMode(true)}
                  className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium transition-colors duration-200"
                >
                  <FaCheckSquare className="text-sm" />
                  {t('products.selectProducts')}
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {isLoading ? (
              <div className="flex justify-center items-center h-64 col-span-full">
                <svg
                  className="animate-spin h-10 w-10 text-indigo-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
                <span className="ml-2 text-gray-500">{t('products.loading')}</span>
              </div>
            ) : products.length > 0 ? (
              products.map((product) => {
                // Extract article from product for selection
                let article;
                if (product.articles && Array.isArray(product.articles)) {
                  const firstArticle = product.articles[0];
                  // Handle images - can be array directly (search results) or object with M property (regular products)
                  let imagesArray = [];
                  if (Array.isArray(firstArticle.images)) {
                    imagesArray = firstArticle.images;
                  } else if (Array.isArray(firstArticle.images?.M)) {
                    imagesArray = firstArticle.images.M;
                  }
                  
                  article = {
                    pharmacode: firstArticle.pharmacode,
                    description: {
                      description: firstArticle.description?.description || 'No description available',
                      longDescription: firstArticle.description?.longDescription || 'No description available'
                    },
                    hasPhoto: firstArticle.hasPhoto || false,
                    images: {
                      M: imagesArray.map(img => img.replace(/^uploads\\/, ''))
                    },
                    price: firstArticle.price || firstArticle.prices || firstArticle.priceInfo || null
                  };
                } else {
                  const description = product.description;
                  let descriptionText = 'No description available';
                  let longDescriptionText = 'No long description available';
                  
                  if (typeof description === 'string') {
                    descriptionText = description;
                    longDescriptionText = description;
                  } else if (description && typeof description === 'object') {
                    descriptionText = description.description || description.longDescription || 'No description available';
                    longDescriptionText = description.longDescription || description.description || 'No long description available';
                  }
                  
                  article = {
                    pharmacode: product.pharmacode || product.productNumber || product._id,
                    description: {
                      description: descriptionText,
                      longDescription: longDescriptionText
                    },
                    hasPhoto: product.hasPhoto || false,
                    images: {
                      M: Array.isArray(product.images)
                        ? product.images.map(img => img.replace(/^uploads\\/, ''))
                        : Array.isArray(product.images?.M)
                          ? product.images.M.map(img => img.replace(/^uploads\\/, ''))
                          : []
                    },
                    price: product.prices || product.priceInfo || null
                  };
                }

                const isSelected = selectedProducts.some(selected => selected.pharmacode === article.pharmacode);

                return (
                  <ProductCard
                    key={product._id}
                    product={product}
                    onEdit={(article) => handleEditClick(article, product)}
                    onDelete={handleDeleteClick}
                    onSelect={handleSelectProduct}
                    onPriceSaved={handlePriceSaved}
                    isSelected={isSelected}
                    isSelectionMode={isSelectionMode}
                  />
                );
              })
            ) : (
              <div className="text-center col-span-full">
                <h3 className="text-lg font-medium text-gray-900">{t('products.empty.title')}</h3>
                <p className="text-gray-500">
                  {isSearchMode 
                    ? t('products.empty.search')
                    : isCategoryMode 
                    ? t('products.empty.category')
                    : isSupplyCategoryMode
                    ? t('products.empty.supplyCategory')
                    : isMiGeLMode
                    ? t('products.empty.migel')
                    : isATCMode
                    ? t('products.empty.atc')
                    : isGenerikaMode
                    ? t('products.empty.generika')
                    : isCompanyMode
                    ? t('products.empty.company')
                    : t('products.empty.default')
                  }
                </p>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalProducts={totalProducts}
              isLoading={isLoading}
              showProductInfo={true}
            />
          )}
        </section>
        </>
        )}
      </div>

      {/* Edit Price Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
               onClick={() => setShowEditModal(false)} 
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-white rounded-lg max-w-lg w-full z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-medium mb-1">{t('products.modals.editPrice.title')}</h3>
                {/* Show the pharmacode being edited so it's obvious which
                    article we're targeting (helps when the unpriced list
                    surfaces items with similar names). */}
                {selectedProduct?.pharmacode != null && (
                  <p className="text-xs text-gray-500 mb-4">
                    Pharmacode: <span className="font-mono">{String(selectedProduct.pharmacode)}</span>
                  </p>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('products.modals.editPrice.label')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    className="w-full border rounded-md p-2"
                  />
                </div>
                {/* In-modal error display — the global error banner at the
                    top of the page is hidden behind the modal backdrop, so
                    failures used to look like "nothing happened." */}
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                    {error}
                  </div>
                )}
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => { setError(null); setShowEditModal(false); }}
                    className="px-4 py-2 border rounded-md"
                  >
                    {t('products.modals.editPrice.cancel')}
                  </button>
                  <button
                    onClick={handleUpdatePrice}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    {t('products.modals.editPrice.update')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
               onClick={() => setShowDeleteModal(false)}
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-lg max-w-lg w-full p-6 z-50"
                 onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium mb-4">{t('products.modals.delete.title')}</h3>
              <textarea
                placeholder={t('products.modals.delete.placeholder')}
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="w-full border rounded-md p-2 mb-4"
                rows="3"
              />
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  {t('products.modals.delete.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={`px-4 py-2 rounded-md transition-all duration-200 ${
                    isDeleting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-red-500 hover:bg-red-600'
                  } text-white flex items-center gap-2`}
                >
                  {isDeleting ? (
                    <>
                      <LoadingSpinner size="sm" variant="white" />
                      {t('products.modals.delete.deleting')}
                    </>
                  ) : (
                    t('products.modals.delete.delete')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
               onClick={() => setIsBulkDeleteModalOpen(false)}
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-white rounded-lg max-w-lg w-full z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-medium mb-4">{t('products.modals.bulkDelete.title')}</h3>
                <p className="text-sm text-gray-700 mb-4">
                  {t('products.modals.bulkDelete.description', { count: selectedProducts.length })}
                </p>
                <textarea
                  placeholder={t('products.modals.bulkDelete.placeholder')}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full border rounded-md p-2 mb-4"
                  rows="3"
                />
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setIsBulkDeleteModalOpen(false)}
                    className="px-4 py-2 border rounded-md"
                  >
                    {t('products.modals.bulkDelete.cancel')}
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className={`px-4 py-2 rounded-md transition-all duration-200 ${
                      isBulkDeleting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-red-500 hover:bg-red-600'
                    } text-white flex items-center gap-2`}
                  >
                    {isBulkDeleting ? (
                      <>
                        <LoadingSpinner size="sm" variant="white" />
                        {t('products.modals.bulkDelete.deleting')}
                      </>
                    ) : (
                      t('products.modals.bulkDelete.delete')
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Import products from file */}
      <ProductImportModal
        isOpen={showProductImportModal}
        onClose={() => setShowProductImportModal(false)}
        onSuccess={() => { refreshProductList(); }}
      />

      {/* Add Bestseller Modal */}
      {showAddBestsellerModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowAddBestsellerModal(false)} 
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl max-w-md w-full z-50 shadow-2xl border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                    <FaEdit className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('products.modals.addBestseller.title')}</h2>
                    <p className="text-sm text-gray-500">{t('products.modals.addBestseller.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddBestsellerModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                    {error}
                  </div>
                )}
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('products.modals.addBestseller.label')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <ProductAutocompleteInput
                    value={newBestsellerPharmacode}
                    onChange={val => setNewBestsellerPharmacode(val)}
                    onSelectProduct={product => setNewBestsellerPharmacode(product._id || product.pharmacode || product.productNumber)}
                    onShowMoreResults={term => { setProductSearchTerm(term); }}
                    placeholder={t('products.search.placeholder')}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">{t('products.modals.addBestseller.hint')}</p>
              </div>
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl">
                <button
                  onClick={() => {
                    setShowAddBestsellerModal(false);
                    setNewBestsellerPharmacode('');
                  }}
                  disabled={isAddingBestseller}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('products.modals.addBestseller.cancel')}
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (!newBestsellerPharmacode) {
                        setError(t('products.errors.enterPharmacode'));
                        return;
                      }
                      setIsAddingBestseller(true);
                      setError(null);
                      await addBestseller(newBestsellerPharmacode);
                      // Refresh current view
                      if (isBestsellerMode) {
                        await handlePageChange(1);
                      } else {
                        await fetchProducts(currentPage);
                      }
                      setShowAddBestsellerModal(false);
                      setNewBestsellerPharmacode('');
                    } catch (err) {
                      setError(err?.response?.data?.message || t('products.errors.addBestsellerFailed'));
                    } finally {
                      setIsAddingBestseller(false);
                    }
                  }}
                  disabled={isAddingBestseller}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isAddingBestseller ? t('products.modals.addBestseller.adding') : t('products.modals.addBestseller.add')}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Add Discount Modal */}
      {showAddDiscountModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowAddDiscountModal(false)} 
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl max-w-md w-full z-50 shadow-2xl border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-lg">
                    <FaEdit className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('products.modals.addDiscount.title')}</h2>
                    <p className="text-sm text-gray-500">{t('products.modals.addDiscount.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddDiscountModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                    {error}
                  </div>
                )}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('products.modals.addDiscount.pharmacodeLabel')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <ProductAutocompleteInput
                        value={newDiscount.pharmacode}
                        onChange={val => setNewDiscount({ ...newDiscount, pharmacode: val })}
                        onSelectProduct={product => setNewDiscount({ ...newDiscount, pharmacode: product._id || product.pharmacode || product.productNumber })}
                        onShowMoreResults={term => { setProductSearchTerm(term); }}
                        placeholder={t('products.search.placeholder')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('products.modals.addDiscount.priceLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newDiscount.price}
                      onChange={(e) => setNewDiscount({ ...newDiscount, price: e.target.value })}
                      placeholder={t('products.modals.addDiscount.pricePlaceholder')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl">
                <button
                  onClick={() => {
                    setShowAddDiscountModal(false);
                    setNewDiscount({ pharmacode: '', price: '' });
                  }}
                  disabled={isAddingDiscount}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('products.modals.addDiscount.cancel')}
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (!newDiscount.pharmacode || !newDiscount.price) {
                        setError(t('products.errors.fillAllFields'));
                        return;
                      }
                      setIsAddingDiscount(true);
                      setError(null);
                      const resp = await updateProductDiscount(newDiscount.pharmacode, parseFloat(newDiscount.price));
                      if (resp.success) {
                        if (isDiscountMode) {
                          await refreshDiscountView(1);
                        } else {
                          await fetchProducts(currentPage);
                        }
                        setShowAddDiscountModal(false);
                        setNewDiscount({ pharmacode: '', price: '' });
                      }
                    } catch (err) {
                      if (err?.response?.data?.error && typeof err.response.data.error === 'string' && err.response.data.error.toLowerCase().includes('ppub price not found')) {
                        setError(t('products.errors.noPublicPrice'));
                      } else {
                        setError(err?.response?.data?.message || t('products.errors.addDiscountFailed'));
                      }
                    } finally {
                      setIsAddingDiscount(false);
                    }
                  }}
                  disabled={isAddingDiscount}
                  className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:from-red-600 hover:to-pink-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isAddingDiscount ? t('products.modals.addDiscount.adding') : t('products.modals.addDiscount.add')}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Create / edit manual product */}
      <ProductFormModal
        isOpen={showProductFormModal}
        mode={productFormMode}
        product={productFormProduct}
        onClose={() => setShowProductFormModal(false)}
        onSaved={handleProductFormSaved}
      />
    </div>
  );
};

export default ProductManager;
